import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../../contexts/useLanguage';
import { FluidTabs } from '../../ui/fluid-tabs';

const HomeMapSection: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'fishing' | 'stays' | 'findus'>('findus');

  const outfitterAddress = t('landing.contact.addressValue');

  const mapSource = useMemo(() => {
    if (activeTab === 'fishing') {
      return 'https://www.google.com/maps?q=46.784962264890886,-74.00768976524084&z=12&output=embed';
    }
    if (activeTab === 'stays') {
      return 'https://www.google.com/maps?q=46.784962264890886,-74.00768976524084&z=13&output=embed';
    }
    // "Find us": query by full address so Google pins the actual property (same as footer contact).
    return `https://www.google.com/maps?q=${encodeURIComponent(outfitterAddress)}&z=16&output=embed`;
  }, [activeTab, outfitterAddress]);

  const mapMeta = {
    fishing: {
      title: t('landing.map.fishingTitle') || 'Fishing spots',
      desc: t('landing.map.fishingDesc') || 'Recommended zones for guided and self-service fishing.',
    },
    stays: {
      title: t('landing.map.staysTitle') || 'Stays sites',
      desc: t('landing.map.staysDesc') || 'Main chalet, yurt and unit locations.',
    },
    findus: {
      title: t('landing.map.findUsTitle') || 'Find us',
      desc: t('landing.map.findUsDesc') || 'Main address and road access.',
    },
  }[activeTab];

  return (
    <section id="map" className="bg-[#efe7df] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="font-felix-titling mb-2 text-3xl font-bold text-[#3d2817] md:text-4xl">
          {t('landing.map.title') || 'Map'}
        </h2>
        <p className="font-felix-titling mb-5 max-w-full truncate overflow-hidden text-ellipsis whitespace-nowrap text-xl text-gray-600">
          {t('landing.map.subtitle') || 'A lightweight map section for visitors to quickly locate the site.'}
        </p>

        <FluidTabs
          tabs={[
            { id: 'findus', label: t('landing.map.tabFindUs') || 'Find us' },
            { id: 'fishing', label: t('landing.map.tabFishingSpots') || 'Fishing spots' },
            { id: 'stays', label: t('landing.map.tabStaysSites') || 'Stays sites' },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as 'fishing' | 'stays' | 'findus')}
          className="mb-6"
        />

        <div className="mb-4 rounded-xl border border-[#d7c8b7] bg-white/80 p-4">
          <p className="text-sm font-semibold text-[#3d2817]">{mapMeta.title}</p>
          <p className="text-sm text-gray-600">{mapMeta.desc}</p>
          {activeTab === 'findus' ? (
            <p className="mt-3 text-sm font-medium leading-relaxed text-[#3d2817]">{outfitterAddress}</p>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-2xl border border-[#d7c8b7] bg-white shadow-sm">
          <iframe
            title="Camp location map"
            className="h-[320px] w-full md:h-[420px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={mapSource}
          />
        </div>
      </div>
    </section>
  );
};

export default HomeMapSection;
